import type { Sphere } from 'three';

/** A painted mesh, whose vertices the painter warp moves in the vertex shader. */
export interface WarpMesh {
  frustumCulled: boolean;
  /** Present on an InstancedMesh: the bounds of its instances, computed when it is next culled
   *  while null. */
  boundingSphere?: Sphere | null;
}

/**
 * The warp's hold on culling. While the painter warp moves vertices, the bounds the CPU culls by no
 * longer hold, so every painted mesh is drawn; `own` keeps each mesh's own setting, in the order of
 * the meshes, for the moment the warp lets go.
 */
export interface WarpCulling {
  readonly active: boolean;
  readonly own: readonly boolean[];
}

export const WARP_CULLING_RELEASED: WarpCulling = { active: false, own: [] };

/* A mesh that culls itself is culled by bounds that were computed before or during the warp; its
   instances may have moved since, so they are dropped and recomputed at the next cull. A mesh that
   turned culling off keeps it off: it moves its instances without keeping bounds. */
function release(meshes: readonly WarpMesh[], own: readonly boolean[]): void {
  meshes.forEach((mesh, i) => {
    const culls = own[i] ?? mesh.frustumCulled;
    mesh.frustumCulled = culls;
    if (culls && mesh.boundingSphere !== undefined) mesh.boundingSphere = null;
  });
}

export function updateWarpCulling(
  meshes: readonly WarpMesh[],
  culling: WarpCulling,
  warpActive: boolean
): WarpCulling {
  if (warpActive === culling.active) return culling;
  if (!warpActive) {
    release(meshes, culling.own);
    return WARP_CULLING_RELEASED;
  }
  const own = meshes.map((mesh) => mesh.frustumCulled);
  for (const mesh of meshes) mesh.frustumCulled = false;
  return { active: true, own };
}
